/**
 * El chat de WhatsApp, listo para montar en cualquier lado.
 *
 * POR QUÉ EXISTE. El chat completo vivía solo dentro de la bandeja de Chats, atado a su layout de
 * tres columnas, a su lista de conversaciones y a su ruta. Cualquier otra pantalla que quisiera
 * conversar —la lista de pedidos, un lead del CRM, la ficha de un cliente— tenía dos caminos:
 * mandar al usuario a otra pantalla, o recrear el chat. Los dos son malos, y el segundo produce una
 * imitación a la que le faltan los checks, las reacciones y los audios.
 *
 * Esto es el chat de verdad —mismo hilo, mismo compositor, mismo fondo, mismos avatares— sin
 * cabecera y sin panel lateral, para que quien lo monte le ponga el marco que le corresponda:
 * un modal, una ventana del dock, una columna.
 *
 * 🪤 SE RESUELVE POR TELÉFONO, NO POR ID DE CONVERSACIÓN. Quien llama desde un pedido o un lead
 * tiene el teléfono del cliente, no el id de un chat que quizá ni existe. Y se busca por los
 * ÚLTIMOS 9 DÍGITOS: el pedido guarda el número como lo tipeó quien lo cargó ("999888777",
 * "+51 999 888 777", "51999888777") y WhatsApp lo guarda a su manera. Comparar el texto completo
 * no encuentra nada la mitad de las veces.
 *
 * 🪤 NO HABER ENCONTRADO CONVERSACIÓN NO ES UN ERROR. Es un cliente que todavía no escribió. Se
 * dice con esas palabras, no con un banner rojo.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner, Banner, Text } from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useWaMessages } from '@/hooks/useWaMessages';
import { useWaComposerHandlers } from '@/hooks/useWaComposerHandlers';
import { useWaReactions } from '@/hooks/useWaReactions';
import { useWaLineAvatar } from '@/hooks/useWaLineAvatar';
import { WaMessageThread, type WaMessage } from '@/components/whatsapp/WaMessageThread';
import { WaChatComposer } from '@/components/whatsapp/WaChatComposer';
import { waChatBgStyle } from '@/components/whatsapp/wa-chat-bg';

export interface WaChatEmbebidoProps {
  tenantId: string;
  /** Teléfono del cliente, en cualquier formato. */
  telefono: string | null;
  /** Solo para mostrar mientras no haya nombre en la conversación. */
  nombreCliente?: string | null;
  /**
   * Avisa la foto de perfil apenas se resuelve la conversación, para que el marco de afuera pueda
   * ponerla en su cabecera. Se le pasa al padre en vez de que él haga su propia consulta: la foto
   * ya viene en la misma fila que se leyó para encontrar el chat.
   */
  onContactoResuelto?: (datos: { fotoUrl: string | null }) => void;
}

export function WaChatEmbebido({ tenantId, telefono, nombreCliente, onContactoResuelto }: WaChatEmbebidoProps) {
  const { user } = useAuth();
  const [buscando, setBuscando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationStatus, setConversationStatus] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<WaMessage | null>(null);
  const [fotoContacto, setFotoContacto] = useState<string | null>(null);
  const [phoneLineId, setPhoneLineId] = useState<string | null>(null);

  const avatarPropio = useWaLineAvatar(tenantId, phoneLineId);

  /*
   * 🪤 EL CALLBACK VA POR REF, NO EN LAS DEPENDENCIAS. Quien nos monta suele pasarlo inline, así que
   * cambia de identidad en cada render; si estuviera en las deps de `buscarConversacion`, la
   * búsqueda se repetiría sin fin.
   */
  const avisarContacto = useRef(onContactoResuelto);
  avisarContacto.current = onContactoResuelto;

  const {
    messages, setMessages, msgLoading,
    hasOlderMessages, loadingOlder, handleLoadOlderMessages, messagesEndRef,
  } = useWaMessages({ conversationId, tenantId });

  const enviarReaccion = useWaReactions({ conversationId, messages, setMessages });

  const { sendText, sendMedia, sendMediaUrl, retryMessage } = useWaComposerHandlers({
    tenantId,
    userId: user?.id,
    selectedConvId: conversationId,
    messages,
    setMessages,
    messagesEndRef,
    replyingTo,
    setReplyingTo,
    // Sin `setConversations`: acá no hay lista de chats que reordenar. El hook lo contempla.
  });

  const buscarConversacion = useCallback(async () => {
    if (!telefono) { setBuscando(false); setError('Este cliente no tiene teléfono cargado.'); return; }
    setBuscando(true);
    setError(null);

    const ultimos9 = telefono.replace(/\D/g, '').slice(-9);
    const { data, error: e } = await supabase
      .from('wa_conversations')
      .select('id, status, contact_photo_url, phone_line_id')
      .eq('tenant_id', tenantId)
      .like('contact_phone', `%${ultimos9}`)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    setBuscando(false);
    if (e) { setError(e.message); return; }
    const foto = (data?.contact_photo_url as string) ?? null;
    setConversationId((data?.id as string) ?? null);
    setConversationStatus((data?.status as string) ?? null);
    setFotoContacto(foto);
    setPhoneLineId((data?.phone_line_id as string) ?? null);
    avisarContacto.current?.({ fotoUrl: foto });
  }, [telefono, tenantId]);

  useEffect(() => { void buscarConversacion(); }, [buscarConversacion]);

  /*
   * El copiloto del compositor sugiere respuesta a partir del último mensaje ENTRANTE del cliente.
   * Las notas internas no cuentan: las escribe el equipo, no el cliente.
   */
  const ultimoEntrante = useMemo(
    () => messages.filter((m) => m.direction === 'inbound' && !m.is_internal_note).at(-1)?.body ?? null,
    [messages],
  );

  if (buscando) {
    return (
      <div style={{ ...waChatBgStyle, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner accessibilityLabel="Buscando la conversación" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...waChatBgStyle, flex: 1, padding: 12, overflowY: 'auto' }}>
        <Banner tone="critical"><Text as="p">{error}</Text></Banner>
      </div>
    );
  }

  if (!conversationId) {
    return (
      <div style={{
        ...waChatBgStyle, flex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <div style={{ maxWidth: 280, textAlign: 'center' }}>
          <Text as="p" tone="subdued">
            Este cliente todavía no tiene conversación de WhatsApp. Se crea sola cuando él escriba
            por primera vez.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      ...waChatBgStyle,
      flex: 1, minHeight: 0,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <WaMessageThread
          messages={messages}
          loading={msgLoading}
          onSendReaction={enviarReaccion}
          onRetryMessage={retryMessage}
          onReplyMessage={setReplyingTo}
          messagesEndRef={messagesEndRef}
          hasOlderMessages={hasOlderMessages}
          loadingOlder={loadingOlder}
          onLoadOlder={handleLoadOlderMessages}
          conversationId={conversationId}
          tenantId={tenantId}
          contactName={nombreCliente ?? null}
          contactPhone={telefono ?? undefined}
          contactPhotoUrl={fotoContacto}
          currentUserAvatarUrl={avatarPropio}
          onSendQuickReply={(text) => sendText(text, false)}
        />
      </div>

      <WaChatComposer
        tenantId={tenantId}
        conversationId={conversationId}
        conversationStatus={conversationStatus}
        /*
         * Sin respuestas rápidas guardadas: cargarlas exige su propio fetch y su pantalla de
         * administración, que ya existen en Chats. Con la lista vacía el compositor no muestra el
         * botón — no queda un control muerto.
         */
        quickReplies={[]}
        lastInboundMessage={ultimoEntrante}
        replyingTo={replyingTo ? {
          id: replyingTo.id,
          body: replyingTo.body,
          msg_type: replyingTo.msg_type,
          direction: replyingTo.direction,
          media_url: replyingTo.media_url,
          media_caption: replyingTo.media_caption,
        } : null}
        onCancelReply={() => setReplyingTo(null)}
        onSend={sendText}
        onSendMedia={sendMedia}
        onSendMediaUrl={sendMediaUrl}
      />
    </div>
  );
}
